import {
  Entity,
  VisibilityState,
  GameManager,
  MoveMode,
  Shape,
  TextObject,
  V2,
  Room,
  RoomId,
  GameObject,
  Space,
  Transform,
  AnchoredPosition,
  PlaygroundAnimation,
  AnimationType,
  TransformSizeAnimation,
  TransformSize,
  TransformRadius,
  RadiusAnimation,
  EntitySyncType,
  Size,
  AttachPoint,
  type Time,
  type ShapeStyleEntity,
  type FullSizeAnimation,
  type TransformFullSize,
  type PlaygroundAnimationCollection,
  type RotationAnimation,
  type ScaleAnimation,
  type AnchoredAnimation,
  type StyleAnimation,
  type RenderShapeStyle,
  type Color,
  type OpacityAnimation,
  type GameObjectController,
} from './types';
import { getNewEntityId, setVisibilityState } from './simulate';
import {
  cloneAnchoredPos,
  cloneV2,
  hasVisibleState,
  makeDenseArray,
  remapNumber01,
  removeDenseArrayIndex,
  toSize,
  subtractV2,
  v2,
  cloneTransfromSize,
  lerpNumber,
  cloneTransfromRadius,
  addV2,
  distanceBetweenSqr,
  getParentRelationship,
  cloneShapeStyle,
  lerpShapeStyle,
  cloneTransfromFullSize,
} from './helpers';
import { DIRECTION } from './constants';
import { ObjectType } from './shared/api_shared_types';
import { debugState, frameNr } from './index';
import {
  lerpAnchoredPosition,
  lerpTransformFullSize,
  lerpTransformRadius,
  lerpTransformSize,
} from './animation';
import { getZeroSpace } from './space';
import logger from './controllers/logger';

export function addEntity(manager: GameManager, entity: Entity): Entity {
  entity.id = getNewEntityId(manager);
  entity.manager = manager;
  manager.entities.push(entity);

  entity.layoutChanged = true;

  return entity;
}

export function getZeroTransform(): Transform {
  let transform: Transform = {
    layoutChanged: false,
    debug: '',
    pos: { x: 0, y: 0 },
    vel: { x: 0, y: 0 },
    force: { x: 0, y: 0 },
    rotation: 0,
    scale: 1,
    radius: 0,
    width: 0,
    height: 0,
    relativeWidth: 0,
    relativeHeight: 0,
    padWidth: 0,
    padHeight: 0,
    maxWidth: 0,
    maxHeight: 0,
    maxAspect: 0,

    disableAutoSize: false,

    shape: Shape.NONE,
    anchoredPos: anchoredPos(null, v2(), null),
    pivotPoint: { x: 0.5, y: 0.5 },
    childAnchorPivot: { x: 0.5, y: 0.5 },

    influenceKinetic: false,
    pushBack: false,
    pushBackChildren: false,
  };

  return transform;
}

export function getZeroEntity(): Entity {
  let entity: Entity = {
    ...getZeroTransform(),
    manager: null,
    doKill: false,
    id: 0,
    serverObjId: 0,
    isDirty: false,
    parent: null,
    children: [],
    parentRoomId: '',
    moveMode: MoveMode.STATIC,
    isAttached: false,
    animations: [],
    allowHighlight: true,
    allowChildHighlight: true,
    allowDrag: true,
    dragPos: v2(),
    relativeMousePos: v2(),
    dragged: false,
    highlighted: false,
    highlightLastFrame: false,
    startHighlightThisFrame: false,
    stopHighlightThisFrame: false,
    lifeTime: 0,
    localZIndex: 0,
    visibilityState: VisibilityState.NONE,
    entityStateChanged: false,
    stateChangedTime: 0,
    stateProgress: 0,
    autoDisappearTime: 0,
    clickable: false,
    mouseDownOnMe: false,
    mouseDownTime: 0,
    clickedThisFrame: false,
    appearDuration: 0.3,
    disappearDuration: 0.3,
    passHover: false,
    passClick: false,
    passClickCanvas: false,
    passDrag: false,
    droppedThisFrame: false,
    requestTextInput: false,
    fixedZIndex: false,
    alwaysOnTop: false,
    renderShadow: null,
    renderShadowDrag: null,
    appearAnimation: null,
    disappearAnimation: null,
    opacity: 1,
    doClipChildren: false,
    dispose: null,
    showTypeHintOnHover: false,
    mouseOver: false,
    topHighlightedChild: null,
    syncType: EntitySyncType.NONE,
    attachpoints: [],
    attachChild: null,
    detachChild: null,
  };

  entity.attachChild = (child, parent) => {
    setParent(child, parent);
  };

  entity.detachChild = (child, parent) => {
    unParentEntity(child);
  };

  return entity;
}

export function entityDisappear_scale(e: Entity, p: number) {
  if (e.shape === Shape.CIRCLE) {
    // e.radiusScale = 1 - p;
    e.scale = 1 - p;
  } else {
    let minHeightScale = 32 / e.height;
    let schrinkHeightScale = 1 - minHeightScale;
    let t = remapNumber01(p, 0, 0.5, true);
    e.scale = 1 - t * schrinkHeightScale;
    // e.widthScale = 1 - remapNumber01(p, 0.5, 1, true);
  }
}

export function textDisappear_fade(t: TextObject, p: number) {
  for (let i = t.words.length - 1; i >= 0; i--) {
    t.words[i].colorAlpha = (1 - p) * 255;
  }
}

export function getZeroStaticEntity(settings: Partial<Entity> = {}): Entity {
  let entity: Entity = {
    ...getZeroEntity(),
    allowHighlight: false,
    allowDrag: false,
    ...settings,
  };

  return entity;
}

export function getZeroAnchoredEntity(settings: Partial<Entity> = {}): Entity {
  let entity: Entity = {
    ...getZeroEntity(),
    allowHighlight: false,
    allowDrag: false,
    moveMode: MoveMode.ANCHORED,
    anchoredPos: anchoredPos(v2(0, 0), v2(0, 0), v2(0, 0)),
    ...settings,
  };

  return entity;
}

export function getZeroKinticEntity(): Entity {
  let entity: Entity = {
    ...getZeroEntity(),
    allowHighlight: true,
    allowDrag: true,
    influenceKinetic: true,
    moveMode: MoveMode.KINETIC,
  };

  return entity;
}

export function getEntityPositionXY(
  entity: Entity,
  anchoredPosition: AnchoredPosition = null,
  referenceEntity: Transform = null,
): [number, number] {
  if (!anchoredPosition) {
    if (!entity.anchoredPos) {
      console.error('No anchored position was given.', entity);
      return [entity.pos.x, entity.pos.y];
    } else {
      anchoredPosition = entity.anchoredPos;
    }
  }

  if (!referenceEntity) {
    if (anchoredPosition.referenceId > 0) {
      referenceEntity = entity.parent.children.find((it) => it.id === anchoredPosition.referenceId);
    }
    if (!referenceEntity) {
      referenceEntity = entity.parent;
    }
  }

  //offset based on alignment with parent
  let anchoredOffsetX = 0;
  let anchoredOffsetY = 0;

  //offset based on transform's own pivot point
  let pivotOffsetX = 0;
  let pivotOffsetY = 0;

  //general offset
  let offsetX = 0;
  let offsetY = 0;

  if (anchoredPosition) {
    //calculate general offset
    if (anchoredPosition.offset) {
      offsetX = anchoredPosition.offset.x;
      offsetY = anchoredPosition.offset.y;
    }

    //calculate transform's own pivot point, relative to size
    if (anchoredPosition.relativePivotPos) {
      const myWidth = entity.width;
      const myHeight = entity.height;
      pivotOffsetX = (1 - anchoredPosition.relativePivotPos.x - 0.5) * myWidth;
      pivotOffsetY = (1 - anchoredPosition.relativePivotPos.y - 0.5) * myHeight;
    }

    //calculate position relative to reference entity, relative to it's size
    if (referenceEntity) {
      const parentWidth = referenceEntity.width;
      const parentHeight = referenceEntity.height;

      let childAnchorPivotX = 0.5;
      let childAnchorPivotY = 0.5;

      if (anchoredPosition.relativePos) {
        childAnchorPivotX = anchoredPosition.relativePos.x;
        childAnchorPivotY = anchoredPosition.relativePos.y;
      } else if (referenceEntity.childAnchorPivot) {
        childAnchorPivotX = referenceEntity.childAnchorPivot.x;
        childAnchorPivotY = referenceEntity.childAnchorPivot.y;
      }

      anchoredOffsetX = (-0.5 + childAnchorPivotX) * parentWidth;
      anchoredOffsetY = (-0.5 + childAnchorPivotY) * parentHeight;
    }
  }

  const x = anchoredOffsetX + pivotOffsetX + offsetX;
  const y = anchoredOffsetY + pivotOffsetY + offsetY;

  return [x, y];
}

export function addGameObject<T extends GameObject>(
  manager: GameManager,
  gameObject: T,
  list: T[] | null = null,
) {
  let exists = gameObject.id > 0 && manager.gameObjectIdTable[gameObject.id];

  if (!exists) {
    addEntity(manager, gameObject);
    manager.gameObjectIdTable[gameObject.id] = gameObject;

    // Register in registry by object type
    const objectType = (gameObject as any).objectType;
    if (objectType !== undefined) {
      let typeSet = manager.registry.byType.get(objectType);
      if (!typeSet) {
        typeSet = new Set();
        manager.registry.byType.set(objectType, typeSet);
      }
      typeSet.add(gameObject);
    }

    if (list) {
      list.push(gameObject);
    }
  } else {
    logger.trace('This gamobject id is already added', {
      obj: logGameObjectSimple(gameObject),
      gameObjectIdTable: manager.gameObjectIdTable,
    });
  }
}

export function getObjectsByType<T extends GameObject>(
  manager: GameManager,
  objectType: ObjectType,
): Set<T> {
  const set = manager.registry.byType.get(objectType);
  return (set as Set<T>) || new Set<T>();
}

export function getObjectsByTypeAndServerId<T extends GameObject>(
  manager: GameManager,
  objectType: ObjectType,
  serverId: number,
): T | null {
  const set = manager.registry.byType.get(objectType);
  if (set) {
  }
  return null;
}

export function getFirstObjectByType<T extends GameObject>(
  manager: GameManager,
  objectType: ObjectType,
): T | null {
  const set = manager.registry.byType.get(objectType);
  if (set && set.size > 0) {
    return set.values().next().value as T;
  }
  return null;
}

export function getObjectCountByType(manager: GameManager, objectType: ObjectType): number {
  const set = manager.registry.byType.get(objectType);
  return set ? set.size : 0;
}

export function setParent(gameObject: GameObject, parent: GameObject, localPos: V2 = null) {
  if (!gameObject || !parent) {
    logger.error('SetParent, object or parent is null');
    return;
  }

  if (gameObject.parent === parent) {
    //That was easy!
    return;
  }

  const parentList = getEntityHierarchyList(parent);
  const parentWithSameId = parentList.find((it) => it === gameObject);
  if (parentWithSameId) {
    console.warn('cannot parent entity, hierarchy loop detected. Parent to room instead.');

    return;
  }

  if (gameObject.parent) {
    unParentEntity(gameObject);
  }

  gameObject.parent = parent;

  if (!gameObject.manager) {
    logger.error('Entity has no manager');
    return;
  }

  if (parent.objectType !== ObjectType.ROOM) {
    setEntityRoomId(gameObject, parent.parentRoomId);
  } else {
    setEntityRoomId(gameObject, parent.roomId);
  }

  gameObject.localZIndex = parent.children.length;

  parent.children.push(gameObject);

  if (gameObject.moveMode !== MoveMode.ANCHORED) {
    if (localPos) {
      gameObject.force = v2();
      gameObject.vel = v2();
      setEntityPosition(gameObject, localPos.x, localPos.y);
    } else {
      const newPos = subtractV2(gameObject.pos, getAbsolutePos(parent));
      setEntityPosition(gameObject, newPos.x, newPos.y);
    }
  }
}

export function resetEntityParentToRoom(gameObject: GameObject) {
  unParentEntity(gameObject);

  const manager = gameObject.manager;

  let room: Room = null;
  for (const r of getObjectsByType<Room>(manager, ObjectType.ROOM)) {
    if (r.roomId === gameObject.parentRoomId) {
      room = r;
      break;
    }
  }

  if (!room) {
    console.error(
      `Room with id ${gameObject.parentRoomId} of Entity ${gameObject.id} cannot be found.`,
    );
  } else {
    room.attachChild(gameObject, room);
  }
}

export function unParentEntity(entity: Entity) {
  if (!entity.parent) {
    return;
  }

  const oldParent = entity.parent;
  const oldLocalZIndex = entity.localZIndex;
  const globalEntityPos = getAbsolutePos(entity);

  if (!oldParent) return;

  entity.parent = null;

  makeDenseArray(oldParent.children, (it) => it === entity);

  //
  // setEntityPos(entity, globalEntityPos.x, globalEntityPos.y);

  if (oldParent.children.length === 0) {
    return;
  }

  //Shift zIndex_local of old parents children
  for (let i = 0; i < oldParent.children.length; i++) {
    let child = oldParent.children[i];
    if (child.localZIndex > oldLocalZIndex) {
      child.localZIndex--;
    }
  }
}

export function swapEntityZIndex(entityA: Entity, entityB: Entity) {
  entityA.localZIndex = entityB.localZIndex;
  entityB.localZIndex = entityA.localZIndex;
}

export function moveEntityToBack(entity: Entity) {
  const parent = entity.parent;
  const newLocalZIndex = 0;
  const oldLocalZIndex = entity.localZIndex;

  const childCount = parent.children.length;

  for (let i = 0; i < childCount; i++) {
    const child = parent.children[i];
    if (child.localZIndex >= newLocalZIndex && child.localZIndex < oldLocalZIndex) {
      child.localZIndex++;
    }
  }
  entity.localZIndex = newLocalZIndex;
}

export function moveEntityToFront(entity: Entity) {
  if (entity.fixedZIndex) return;

  const parent = entity.parent;
  const newLocalZIndex = parent.children.length - 1;
  const oldLocalZIndex = entity.localZIndex;

  const childCount = parent.children.length;

  for (let i = 0; i < childCount; i++) {
    const child = parent.children[i];
    if (child.localZIndex > oldLocalZIndex) {
      child.localZIndex--;
    }
  }
  entity.localZIndex = newLocalZIndex;
}

export function getEntityStateAndProgress(entity: Entity): {
  state: VisibilityState;
  progress: number;
} {
  if (!entity.parent) {
    return {
      state: entity.visibilityState,
      progress: entity.stateProgress,
    };
  }

  let parentState = getEntityStateAndProgress(entity.parent);

  let parentStateVisible = hasVisibleState(parentState.state);
  // let childStateVisible = getStateIsVisible( entity.state );

  if (parentState.state === entity.visibilityState) {
    if (!parentStateVisible) {
      return {
        state: parentState.state,
        progress: Math.max(parentState.progress, entity.stateProgress),
      };
    } else {
      return {
        state: parentState.state,
        progress: Math.min(parentState.progress, entity.stateProgress),
      };
    }
  } else {
    let parentStateIsLeading = true;
    if (
      entity.visibilityState === VisibilityState.HIDDEN ||
      entity.visibilityState === VisibilityState.NONE
    ) {
      parentStateIsLeading = false;
    } else {
      parentStateIsLeading =
        !hasVisibleState(parentState.state) ||
        (entity.visibilityState === VisibilityState.DEFAULT &&
          parentState.state !== VisibilityState.DEFAULT);
    }
    if (parentStateIsLeading) {
      return parentState;
    } else {
      return {
        state: entity.visibilityState,
        progress: entity.stateProgress,
      };
    }
  }
}

export function getEntityState(entity: Entity): VisibilityState {
  if (!entity.parent) {
    return entity.visibilityState;
  }

  let parentState = getEntityState(entity.parent);

  if (parentState === entity.visibilityState) {
    return parentState;
  } else {
    let parentStateIsLeading = true;
    if (entity.visibilityState === VisibilityState.HIDDEN) {
      parentStateIsLeading = false;
    } else {
      let parentStateVisible = hasVisibleState(parentState);
      parentStateIsLeading =
        !parentStateVisible ||
        (entity.visibilityState === VisibilityState.DEFAULT &&
          parentState !== VisibilityState.DEFAULT);
    }
    if (parentStateIsLeading) {
      return parentState;
    } else {
      return entity.visibilityState;
    }
  }
}

export function getHoverParentEntities(entity: Entity, entities: Entity[]) {
  if (entity.allowHighlight) {
    entities.push(entity);
  }
  if ((!entity.allowHighlight || entity.passHover) && entity.parent) {
    getHoverParentEntities(entity.parent, entities);
  }
  return;
}

export function getEntityToDrag(entity: Entity): Entity {
  if (((!entity.allowHighlight && !entity.allowDrag) || entity.passDrag) && entity.parent) {
    return getEntityToDrag(entity.parent);
  } else if (entity.allowDrag) {
    return entity;
  } else {
    return null;
  }
}

export function getEntityToClick(entity: Entity): Entity {
  const passHighlight = !entity.allowHighlight || entity.passHover;
  const passClick = passHighlight && !entity.clickable;
  if ((passClick || entity.passClick) && entity.parent) {
    return getEntityToClick(entity.parent);
  } else if (entity.clickable) {
    return entity;
  } else {
    return null;
  }
}

export function setEntityRoomId(entity: Entity, roomId: RoomId) {
  entity.parentRoomId = roomId;

  const childCount = entity.children.length;
  for (let i = 0; i < childCount; i++) {
    setEntityRoomId(entity.children[i], roomId);
  }
}

export function changeRoomForObject(gameObject: GameObject, newRoomId: RoomId) {
  let room: Room = null;
  for (const r of getObjectsByType<Room>(gameObject.manager, ObjectType.ROOM)) {
    if (r.roomId === newRoomId) {
      room = r;
      break;
    }
  }

  if (!room) {
    console.error(`Cannot find room with id ${newRoomId}`);
    return;
  }

  gameObject.parentRoomId = room.roomId;
  setParent(gameObject, room);
}

export function getEntityHierarchyList(entity: Entity): Entity[] {
  let parentList: Entity[] = [entity];

  let checkEnt = entity;
  while (checkEnt.parent) {
    parentList.push(checkEnt.parent);
    checkEnt = checkEnt.parent;
  }

  //Make sure highest parent comes first
  parentList.reverse();

  return parentList;
}

export function isEntityAInFrontOfB(entityA: Entity, entityB, debug = false): boolean {
  let parentListA = getEntityHierarchyList(entityA);
  let parentListB = getEntityHierarchyList(entityB);

  const count = Math.min(parentListA.length, parentListB.length);
  let parentA: Entity;
  let parentB: Entity;

  for (let i = 0; i < count; i++) {
    parentA = parentListA[i];
    parentB = parentListB[i];

    if (parentA.localZIndex < parentB.localZIndex) {
      return false;
    } else if (parentA.localZIndex > parentB.localZIndex) {
      return true;
    }
  }

  if (parentA === parentB) {
    return parentListA.length > parentListB.length;
  } else {
    return true;
  }
}

export function removeGameObject(gameObject: GameObject) {
  removeGameObjectByEntity(gameObject);
}

export function removeGameObjectByEntity(ent: Entity) {
  const manager = ent.manager;

  let entitiesToRemove: Entity[] = [ent];

  let index = 0;

  while (index < entitiesToRemove.length) {
    const entToRemove = entitiesToRemove[index];

    entToRemove.doKill = true;

    if (entToRemove.dragged) {
      ent.manager.cursor.draggedEntity = null;
    }

    for (let i = entToRemove.animations.length - 1; i >= 0; i--) {
      entToRemove.animations[i].doKill = true;
    }

    const gameObjectToRemove = manager.gameObjectIdTable[entToRemove.id];

    if (gameObjectToRemove) {
      const objectType = gameObjectToRemove.objectType;
      const controller = objectType ? manager.objectControllerMap[objectType] : null;
      if (controller?.dispose) {
        controller.dispose(gameObjectToRemove);
      } else if (entToRemove.dispose) {
        entToRemove.dispose(gameObjectToRemove);
      }
    }

    if (entToRemove.id > 0) {
      if (manager.gameObjectIdTable[entToRemove.id]) {
        delete manager.gameObjectIdTable[entToRemove.id];
      } else {
      }
    }
    if (entToRemove.serverObjId > 0) {
      if (manager.gameObjectServerIdTable[entToRemove.serverObjId]) {
        delete manager.gameObjectServerIdTable[entToRemove.serverObjId];
      } else {
      }
    }

    // Remove from registry
    const objectType = (entToRemove as any).objectType;
    if (objectType !== undefined && gameObjectToRemove) {
      const typeSet = manager.registry.byType.get(objectType);
      if (typeSet) {
        typeSet.delete(gameObjectToRemove);
      }
    }

    //if it's parent marked as doKill, don't bother doing child management
    if (entToRemove.parent && !entToRemove.parent.doKill) {
      if (entToRemove.parent.detachChild && gameObjectToRemove) {
        entToRemove.parent.detachChild(gameObjectToRemove, entToRemove.parent);
      } else {
        unParentEntity(entToRemove);
      }
    }

    if (entToRemove.requestTextInput) {
      entToRemove.manager.cursor.inputRequestEntityCount--;
    }

    entitiesToRemove.push(...entToRemove.children);

    index++;
  }
}

export function logEntitySimple(ent: Entity): string {
  return `ent (${ent.serverObjId})`;
}

export function logGameObjectSimple(obj: GameObject): string {
  const id = obj.serverObjId > 0 ? `s${obj.serverObjId}` : obj.id;
  return `${obj.objectType} (${id})`;
}

export function logGameObjectByEntity(ent: Entity): string {
  if (!ent) {
    return 'null';
  }

  const m = ent.manager;

  const gameObject = m.gameObjectIdTable[ent.id];
  if (gameObject) {
    if (gameObject.serverObjId) {
      return `${gameObject.objectType} (s${gameObject.serverObjId})`;
    } else {
      return `${gameObject.objectType} (${gameObject.id})`;
    }
  } else {
    return `ent id: ${ent.id}`;
  }
}

function logGameObjectArraySimple(array: GameObject[]): string[] {
  return array.map((it) => `${it.objectType} (${logEntityId(it)})`);
}

function logEntityArraySimple(array: Entity[]): string[] {
  return array.map((it) => `Entity (${logEntityId(it)})`);
}

function logEntityId(ent: Entity): string {
  return ent.serverObjId > 0 ? `${ent.id}/s${ent.serverObjId}` : `${ent.id}`;
}

export function logRoom(room: Room): string {
  return `${room.name} (${room.roomId})`;
}

export function createSpace(manager: GameManager, ent: Entity): Space {
  let newSpace: Space = getZeroSpace(ent);

  addEntity(manager, newSpace);

  newSpace.pivotPoint = v2();
  newSpace.childAnchorPivot = v2();

  newSpace.allowHighlight = false;

  manager.gameObjectIdTable[newSpace.id] = newSpace;

  newSpace.width = window.innerWidth;
  newSpace.height = window.innerHeight;

  setVisibilityState(newSpace, VisibilityState.DEFAULT);

  return newSpace;
}

export function anchoredPos(
  relativePos: V2 = v2(0.5, 0.5),
  offset = v2(),
  relativePivotPos = v2(0.5, 0.5),
  referenceId: number = 0,
): AnchoredPosition {
  return { referenceId, relativePos, offset, relativePivotPos };
}

export function createPlaygroundAnimation<T>(
  manager: GameManager,
  entity: Entity,
  anim: PlaygroundAnimationCollection,
) {
  anim.t = 0;
  if (anim.duration === 0) {
    anim.duration = 0.01;
  }

  const existingAnimation = entity.animations.find((it) => it.type === anim.type);
  if (existingAnimation) {
    if (anim.type === AnimationType.ANCHORED && existingAnimation.type === AnimationType.ANCHORED) {
      if (!isAnchoredPositionEqual(existingAnimation.end, anim.end)) {
        existingAnimation.doKill = true;
      } else {
        anim.doKill = true;
      }
    } else if (
      anim.type === AnimationType.SIZE_FULL &&
      existingAnimation.type === AnimationType.SIZE_FULL
    ) {
      if (!isFullSizeEqual(existingAnimation.end, anim.end)) {
        existingAnimation.doKill = true;
      } else {
        anim.doKill = true;
      }
    } else if (
      anim.type === AnimationType.SHAPE_STYLE &&
      existingAnimation.type === AnimationType.SHAPE_STYLE
    ) {
      if (!isShapeStyleEqual(existingAnimation.end, anim.end)) {
        existingAnimation.doKill = true;
      } else {
        anim.doKill = true;
      }
    } else {
      existingAnimation.doKill = true;
    }
  }

  entity.animations.push(anim);
  manager.animations.push(anim);
}

export function createTransformSizeAnimation(
  manager: GameManager,
  entity: Entity,
  animation: Partial<TransformSizeAnimation>,
) {
  const widthPx = entity.shape === Shape.CIRCLE ? entity.radius * 2 : entity.width;
  const heightPx = entity.shape === Shape.CIRCLE ? entity.radius * 2 : entity.height;

  const size: TransformSize = {
    sizePx: toSize(widthPx, heightPx),
    sizeEm: null,
  };

  const anim: TransformSizeAnimation = {
    doKill: false,
    type: AnimationType.TRANSFORM_SIZE,
    delay: 0,
    start: size,
    end: size,
    duration: 1,
    t: 0,
    p: 0,
    pEased: 0,
    currentValue: size,
    easeFunction: (t) => t,
    ...animation,
    lerpFunction: lerpTransformSize,
    prop: '',
  };

  anim.currentValue = cloneTransfromSize(anim.start);

  createPlaygroundAnimation(manager, entity, anim);
}

export function createTransformFullSizeAnimation(
  manager: GameManager,
  entity: Entity,
  animation: Partial<FullSizeAnimation>,
) {
  const start = getFullSize(entity);

  const end = animation.end;

  if (!start.relativeHeight && end.relativeHeight) {
    const relativeStartHeight = entity.parent.height > 0 ? start.height / entity.parent.height : 0;
    start.height = 0;
    start.relativeHeight = relativeStartHeight;
  }
  if (!start.relativeWidth && end.relativeWidth) {
    const relativeStartWidth = entity.parent.width > 0 ? start.width / entity.parent.width : 0;
    start.width = 0;
    start.relativeWidth = relativeStartWidth;
  }

  if (start.relativeHeight && !end.relativeHeight) {
    const startHeight = getEntityHeight(entity, entity.parent.height);
    start.height = startHeight;
    start.relativeHeight = 0;
    start.padHeight = 0;
    start.maxHeight = 0;
  }
  if (start.relativeWidth && !end.relativeWidth) {
    const startWidth = getEntityWidth(entity, entity.parent.width);
    start.width = startWidth;
    start.relativeWidth = 0;
    start.padWidth = 0;
    start.maxWidth = 0;
  }

  const anim: FullSizeAnimation = {
    doKill: false,
    type: AnimationType.SIZE_FULL,
    delay: 0,
    start,
    end,
    duration: 1,
    t: 0,
    p: 0,
    pEased: 0,
    currentValue: start,
    easeFunction: (t) => t,
    ...animation,
    lerpFunction: lerpTransformFullSize,
    prop: '',
  };

  anim.currentValue = cloneTransfromFullSize(start);

  createPlaygroundAnimation(manager, entity, anim);
}

export function createTransformRadiusAnimation(
  manager: GameManager,
  entity: Entity,
  animation: Partial<RadiusAnimation>,
) {
  const radius: TransformRadius = {
    radiusPx: entity.radius,
    radiusEm: null,
    // radiusScale: transform.radiusScale,
  };

  const anim: RadiusAnimation = {
    doKill: false,
    type: AnimationType.RADIUS,
    delay: 0,
    start: radius,
    end: radius,
    duration: 1,
    t: 0,
    p: 0,
    pEased: 0,
    currentValue: radius,
    easeFunction: (t) => t,
    ...animation,
    lerpFunction: lerpTransformRadius,
    prop: '',
  };

  anim.currentValue = cloneTransfromRadius(anim.start);

  createPlaygroundAnimation(manager, entity, anim);
}

export function createRotationAnimation(
  manager: GameManager,
  entity: Entity,
  animation: Partial<RotationAnimation>,
) {
  const rot = entity.rotation;

  const anim: RotationAnimation = {
    doKill: false,
    type: AnimationType.ROTATION,
    delay: 0,
    start: rot,
    end: rot,
    duration: 1,
    t: 0,
    p: 0,
    pEased: 0,
    currentValue: rot,
    easeFunction: (t) => t,
    ...animation,
    lerpFunction: lerpNumber,
    prop: '',
  };

  anim.currentValue = anim.start;

  createPlaygroundAnimation(manager, entity, anim);
}

export function createScaleAnimation(
  manager: GameManager,
  entity: Entity,
  animation: Partial<ScaleAnimation>,
) {
  const scale = entity.scale;

  const anim: ScaleAnimation = {
    doKill: false,
    type: AnimationType.SCALE,
    delay: 0,
    start: scale,
    end: scale,
    duration: 1,
    t: 0,
    p: 0,
    pEased: 0,
    currentValue: scale,
    easeFunction: (t) => t,
    ...animation,
    lerpFunction: lerpNumber,
    prop: '',
  };

  anim.currentValue = anim.start;

  createPlaygroundAnimation(manager, entity, anim);
}

export function createOpacityAnimation(
  manager: GameManager,
  entity: Entity,
  animation: Partial<OpacityAnimation>,
) {
  const opacity = entity.opacity;

  const anim: OpacityAnimation = {
    doKill: false,
    type: AnimationType.OPACITY,
    delay: 0,
    start: opacity,
    end: opacity,
    duration: 1,
    t: 0,
    p: 0,
    pEased: 0,
    currentValue: opacity,
    easeFunction: (t) => t,
    ...animation,
    lerpFunction: lerpNumber,
    prop: '',
  };

  anim.currentValue = anim.start;

  createPlaygroundAnimation(manager, entity, anim);
}

export function createTransformAnchoredAnimation(
  manager: GameManager,
  entity: Entity,
  animation: Partial<PlaygroundAnimation<AnchoredPosition>>,
) {
  if (!entity.anchoredPos.relativePos) {
    logger.error('creating default relative pos');
    entity.anchoredPos.relativePos = v2(0.5, 0.5);
    entity.anchoredPos.relativePivotPos = v2(0.5, 0.5);
  }

  const anchoredPosStart = cloneAnchoredPos(entity.anchoredPos);
  const anchoredPosEnd = animation.end;

  if (anchoredPosEnd.relativePos && !anchoredPosStart.relativePos) {
    anchoredPosStart.relativePos = v2();
    anchoredPosStart.relativePivotPos = v2();
  }

  const anim: AnchoredAnimation = {
    doKill: false,
    duration: 1,
    easeFunction: (t) => t,
    delay: 0,
    ...animation,
    type: AnimationType.ANCHORED,
    currentValue: anchoredPosStart,
    start: anchoredPosStart,
    end: anchoredPosEnd,
    t: 0,
    p: 0,
    pEased: 0,
    lerpFunction: lerpAnchoredPosition,
    prop: '',
  };

  //todo: handle this transition
  if (!anim.end.relativePos && anim.start.relativePos) {
    logger.debug('Animate from anchored to non-anchored');
  }

  anim.currentValue = cloneAnchoredPos(anim.start);

  createPlaygroundAnimation(manager, entity, anim);
}

export function createShapeStyleAnimation(
  manager: GameManager,
  entity: ShapeStyleEntity,
  animation: Partial<StyleAnimation>,
) {
  const style = cloneShapeStyle(entity.shapeStyle);

  const anim: StyleAnimation = {
    doKill: false,
    type: AnimationType.SHAPE_STYLE,
    delay: 0,
    start: style,
    end: style,
    duration: 1,
    t: 0,
    p: 0,
    pEased: 0,
    currentValue: style,
    easeFunction: (t) => t,
    ...animation,
    lerpFunction: lerpShapeStyle,
    prop: '',
  };

  anim.currentValue = cloneShapeStyle(anim.start);

  createPlaygroundAnimation(manager, entity, anim);
}

export function updateTransformAnimations(
  entity: Entity,
  animations: PlaygroundAnimationCollection[],
): boolean {
  let isDirty = false;

  for (let i = animations.length - 1; i >= 0; i--) {
    const anim = animations[i];

    if (anim.doKill) {
      anim.doKill = true;
      removeDenseArrayIndex(animations, i);
      continue;
    }

    switch (anim.type) {
      case AnimationType.TRANSFORM_SIZE:
        {
          entity.width = anim.currentValue.sizePx.width;
          entity.height = anim.currentValue.sizePx.height;
          entity.radius = anim.currentValue.sizePx.width / 2;

          isDirty = true;
        }
        break;
      case AnimationType.SIZE_FULL:
        {
          entity.width = anim.currentValue.width;
          entity.height = anim.currentValue.height;
          entity.relativeWidth = anim.currentValue.relativeWidth;
          entity.relativeHeight = anim.currentValue.relativeHeight;
          entity.padWidth = anim.currentValue.padWidth;
          entity.padHeight = anim.currentValue.padHeight;
          entity.maxWidth = anim.currentValue.maxWidth;
          entity.maxHeight = anim.currentValue.maxHeight;
          entity.maxAspect = anim.currentValue.maxAspect;

          isDirty = true;
        }
        break;

      case AnimationType.RADIUS:
        {
          entity.radius = anim.currentValue.radiusPx;

          isDirty = true;
        }
        break;
      case AnimationType.ROTATION:
        {
          entity.rotation = anim.currentValue;
          isDirty = true;
        }
        break;
      case AnimationType.SCALE:
        {
          entity.scale = anim.currentValue;
          isDirty = true;
        }
        break;
      case AnimationType.OPACITY:
        {
          entity.opacity = anim.currentValue;
          isDirty = true;
        }
        break;
      case AnimationType.ANCHORED:
        {
          if (!entity.anchoredPos?.relativePos) {
            logger.error('object has no relativePos', entity);
          } else {
            entity.anchoredPos.relativePos.x = anim.currentValue.relativePos.x;
            entity.anchoredPos.relativePos.y = anim.currentValue.relativePos.y;
            entity.anchoredPos.offset.x = anim.currentValue.offset.x;
            entity.anchoredPos.offset.y = anim.currentValue.offset.y;
            entity.anchoredPos.relativePivotPos.x = anim.currentValue.relativePivotPos.x;
            entity.anchoredPos.relativePivotPos.y = anim.currentValue.relativePivotPos.y;
          }
        }
        break;

      case AnimationType.SHAPE_STYLE:
        {
          if ('shapeStyle' in entity) {
            const shapeStyle = entity.shapeStyle as RenderShapeStyle;
            shapeStyle.fillColor = [...anim.currentValue.fillColor];
            shapeStyle.strokeWeight = anim.currentValue.strokeWeight;
            shapeStyle.strokeColor = [...anim.currentValue.strokeColor];
            shapeStyle.lineDash = [...anim.currentValue.lineDash];
            shapeStyle.lineDashOffset = anim.currentValue.lineDashOffset;
            shapeStyle.gradientId = anim.currentValue.gradientId;
            shapeStyle.gradient = anim.currentValue.gradient;
            shapeStyle.gradientFade = anim.currentValue.gradientFade;

            // entity.shapeStyle = cloneShapeStyle(anim.currentValue);
            isDirty = true;
          }
        }
        break;
    }

    if (anim.t === anim.delay + anim.duration) {
      anim.doKill = true;
      removeDenseArrayIndex(animations, i);
    }
  }

  return isDirty;
}

export function highlightStartedThisFrame(ent: Entity): boolean {
  return ent.highlighted && !ent.highlightLastFrame;
}

export function highlightStoppedThisFrame(ent: Entity): boolean {
  return !ent.highlighted && ent.highlightLastFrame;
}

export function getHalfWidthAndHeight(transform: Transform): Size {
  const width = transform.shape === Shape.CIRCLE ? transform.radius : transform.width / 2;
  const height = transform.shape === Shape.CIRCLE ? transform.radius : transform.height / 2;

  return { width, height };
}

export function getAttachpoints(ent: Entity, filter: string[] = []): AttachPoint[] {
  let allAttachpoints: AttachPoint[];

  if (filter.some((it) => it === 'anchor')) {
    allAttachpoints = ent.attachpoints.map((attachpoint) => {
      const attachpointAbs: AttachPoint = {
        ...attachpoint,
        pos: addV2(ent.pos, attachpoint.pos),
      };
      return attachpointAbs;
    });
  } else if (
    filter.some((it) => it === 'left' || it === 'right' || it === 'top' || it === 'bottom')
  ) {
    allAttachpoints = getDefaultAttachpoints(ent);
  } else if (ent.attachpoints.length) {
    allAttachpoints = ent.attachpoints.map((attachpoint) => {
      const attachpointAbs: AttachPoint = {
        ...attachpoint,
        pos: addV2(ent.pos, attachpoint.pos),
      };
      return attachpointAbs;
    });
  } else {
    allAttachpoints = getDefaultAttachpoints(ent);
  }

  if (filter.length) {
    const filteredAttachpoints = filterAttachpoints(allAttachpoints, filter);

    if (filteredAttachpoints.length) {
      return filteredAttachpoints;
    }
  }
  return allAttachpoints;
}

function filterAttachpoints(attachpoints: AttachPoint[], tags: string[]): AttachPoint[] {
  return attachpoints.filter((it) => tags.some((t) => t === it.tag));
}

export function getDefaultAttachpoints(ent: Entity): AttachPoint[] {
  const x = ent.pos.x;
  const y = ent.pos.y;
  const halfSize = getHalfWidthAndHeight(ent);
  const left: AttachPoint = { pos: v2(x - halfSize.width, y), dir: DIRECTION.LEFT, tag: 'left' };
  const right: AttachPoint = { pos: v2(x + halfSize.width, y), dir: DIRECTION.RIGHT, tag: 'right' };
  const top: AttachPoint = { pos: v2(x, y - halfSize.height), dir: DIRECTION.UP, tag: 'top' };
  const bottom: AttachPoint = {
    pos: v2(x, y + halfSize.height),
    dir: DIRECTION.DOWN,
    tag: 'bottom',
  };

  return [left, right, top, bottom];
}

export function findClosestAttachPoints(
  pointsA: AttachPoint[],
  pointsB: AttachPoint[],
): [AttachPoint, AttachPoint] {
  let minDistance = Infinity;
  let closestPoints: [AttachPoint | null, AttachPoint | null] = [null, null];

  for (const attachPoint1 of pointsA) {
    for (const attachPoint2 of pointsB) {
      const distance = distanceBetweenSqr(attachPoint1.pos, attachPoint2.pos);
      if (distance < minDistance) {
        minDistance = distance;
        closestPoints = [attachPoint1, attachPoint2];
      }
    }
  }

  return closestPoints;
}

export function updateEntityState(ent: Entity, time: Time): boolean {
  if (ent.visibilityState === VisibilityState.APPEARING) {
    let diff = time - ent.stateChangedTime;
    if (diff >= ent.appearDuration) {
      setVisibilityState(ent, VisibilityState.DEFAULT);
    } else {
      ent.stateProgress = diff / ent.appearDuration;
    }
  } else if (
    ent.visibilityState === VisibilityState.DISAPPEARING ||
    ent.visibilityState === VisibilityState.HIDING
  ) {
    let disappearTime = time - ent.stateChangedTime;

    if (disappearTime >= ent.disappearDuration) {
      ent.stateProgress = 1;
      if (ent.visibilityState === VisibilityState.DISAPPEARING) {
        return true;
      } else if (ent.visibilityState === VisibilityState.HIDING) {
        setVisibilityState(ent, VisibilityState.HIDDEN);
      }
    } else {
      ent.stateProgress = disappearTime / ent.disappearDuration;
    }
  } else if (ent.visibilityState === VisibilityState.DEFAULT) {
    //
  }

  return false;
}

export function getEntitySize(
  fullSize: TransformFullSize,
  context: Size,
  debug = false,
): [number, number] {
  let height = getEntityHeight(fullSize, context ? context.height : 0);
  let width = getEntityWidth(fullSize, context ? context.width : 0, debug);

  if (fullSize.maxAspect) {
    const maxWidth = height * fullSize.maxAspect;
    width = Math.min(width, maxWidth);
  }

  return [width, height];
}

export function getEntityWidth(
  size: TransformFullSize,
  contextWidth: number,
  debug = false,
): number {
  let width = size.width;

  if (size.relativeWidth > 0) {
    width = size.relativeWidth * contextWidth + size.padWidth;

    if (size.maxWidth) {
      width = Math.min(width, size.maxWidth);
    }
  }

  return width;
}

export function getEntityHeight(ent: TransformFullSize, contextHeight: number): number {
  let height = ent.height;
  if (ent.relativeHeight > 0) {
    height = ent.relativeHeight * contextHeight + ent.padHeight;
    if (ent.maxHeight) {
      height = Math.min(height, ent.maxHeight);
    }
  }

  return height;
}

export function getEntityRadius(ent: Entity): number {
  let radius = ent.radius;

  if (ent.relativeWidth || ent.relativeHeight) {
    const [w, h] = getEntitySize(ent, ent.parent);

    let size = Math.min(w, h);

    radius = Math.max(size / 2, 0);
  }

  return radius;
}

export function setEntityPosition(ent: Entity, _x: number, _y: number) {
  if (ent.anchoredPos.offset.x !== _x) {
    ent.anchoredPos.offset.x = _x;
  }
  if (ent.anchoredPos.offset.y !== _y) {
    ent.anchoredPos.offset.y = _y;
  }
  // }
}

export function setEntitySize(ent: Entity, _w: number, _h: number) {
  if (ent.width !== _w) {
    ent.width = _w;
    ent.isDirty = true;
  }
  if (ent.height !== _h) {
    ent.height = _h;
    ent.isDirty = true;
  }
}

export function getAbsolutePos(ent: Entity): V2 {
  const [pos, scale] = getAbsolutePosAndScale(ent);

  return pos;
}

export function getAbsolutePosAndScale(ent: Entity, debug = false): [V2, number] {
  const entities = getEntityHierarchyList(ent);

  let pos: V2 = v2();
  let scale = 1;

  for (let i = 0; i < entities.length; i++) {
    const [x, y] = getEntityPositionXY(entities[i]);

    const localScaledPosX = x * scale;
    const localScaledPosY = y * scale;
    pos.x += localScaledPosX;
    pos.y += localScaledPosY;
    scale *= entities[i].scale;
  }

  return [pos, scale];
}

export function getAbsoluteScale(ent: Entity): number {
  const entities = getEntityHierarchyList(ent);

  let scale = 1;

  for (let i = 0; i < entities.length; i++) {
    scale *= entities[i].scale;
  }

  return scale;
}

export function getEntityPosRelativeToEntity(ent: Entity, relativeTo: Entity): V2 {
  const posA = getAbsolutePos(ent);
  const posB = getAbsolutePos(relativeTo);
  return subtractV2(posA, posB);
}

//Returns whether the two entities influence each other kinetically. This could be two-way or one-way
export function entitiesKineticInfluence(entA: Entity, entB: Entity): boolean {
  if (entA === entB) return false;
  if (entA.moveMode !== MoveMode.KINETIC && entB.moveMode !== MoveMode.KINETIC) return false;
  if (entA.moveMode !== MoveMode.KINETIC && !entA.influenceKinetic) return false;
  if (entB.moveMode !== MoveMode.KINETIC && !entB.influenceKinetic) return false;
  // if (checkConnectionTypeBetween(entA, entB) === ConnectionType.MASTER_SLAVE) return false;
  return getParentRelationship(entA, entB) === null;
}

export function entityInfluencesEntity(entA: Entity, entB: Entity): boolean {
  if (entA === entB) return false;
  if (entA.moveMode !== MoveMode.KINETIC && entB.moveMode !== MoveMode.KINETIC) return false;
  if (!entA.influenceKinetic) return false;
  if (entB.moveMode !== MoveMode.KINETIC) return false;
  // if (checkConnectionTypeBetween(entA, entB) === ConnectionType.MASTER_SLAVE) return false;
  // if ( isParentOf( entB, entA ) ) return false;
  return true;
}
export function getAllChildren(entity: Entity): GameObject[] {
  let walkThroughList = [...entity.children];
  let index = 0;
  while (index < walkThroughList.length) {
    let ent = walkThroughList[index];
    if (ent.children) {
      walkThroughList.push(...ent.children);
    }
    index++;
  }
  return walkThroughList;
}

export function isInEntityShape(checkPos: V2, ent: Entity): boolean {
  if (ent.shape === Shape.CIRCLE) {
    let radiusSqr = ent.radius * ent.radius;
    const entPos = getAbsolutePos(ent);
    let distSqr = distanceBetweenSqr(checkPos, entPos);
    return distSqr < radiusSqr;
  } else {
    const entPos = getAbsolutePos(ent);
    return (
      checkPos.x > entPos.x - ent.width / 2 &&
      checkPos.x < entPos.x + ent.width / 2 &&
      checkPos.y > entPos.y - ent.height / 2 &&
      checkPos.y < entPos.y + ent.height / 2
    );
  }
}

export function getEntityPosWithPivot(ent: Entity): V2 {
  const pivot = ent.anchoredPos ? ent.anchoredPos.relativePivotPos : v2(0.5, 0.5);

  const pivotOffsetX = (1 - pivot.x - 0.5) * ent.width;
  const pivotOffsetY = (1 - pivot.y - 0.5) * ent.height;

  const pos = subtractV2(ent.pos, v2(pivotOffsetX, pivotOffsetY));

  if (ent.parent) {
    const result = getAbsolutePos(ent.parent);
    return;
    addV2(result, pos);
  } else {
    return cloneV2(pos);
  }
}

export function getFullSize(transform: Transform): TransformFullSize {
  return {
    width: transform.width,
    height: transform.height,
    relativeWidth: transform.relativeWidth,
    relativeHeight: transform.relativeHeight,
    padWidth: transform.padWidth,
    padHeight: transform.padHeight,
    maxWidth: transform.maxWidth,
    maxHeight: transform.maxHeight,
    maxAspect: transform.maxAspect,
  };
}

export function setFullSize(transform: Transform, fullSize: TransformFullSize) {
  transform.width = fullSize.width;
  transform.height = fullSize.height;
  transform.relativeWidth = fullSize.relativeWidth;
  transform.relativeHeight = fullSize.relativeHeight;
  transform.padWidth = fullSize.padWidth;
  transform.padHeight = fullSize.padHeight;
  transform.maxWidth = fullSize.maxWidth;
  transform.maxHeight = fullSize.maxHeight;
  transform.maxAspect = fullSize.maxAspect;
}

export function isAnchoredPositionEqual(a: AnchoredPosition, b: AnchoredPosition): boolean {
  if (a.relativePos && b.relativePos) {
    if (a.relativePos.x !== b.relativePos.x) return false;
    if (a.relativePos.y !== b.relativePos.y) return false;
  }

  if (a.relativePivotPos && b.relativePivotPos) {
    if (a.relativePivotPos.x !== b.relativePivotPos.x) return false;
    if (a.relativePivotPos.y !== b.relativePivotPos.y) return false;
  }

  if (a.offset.x !== b.offset.x) return false;
  if (a.offset.y !== b.offset.y) return false;

  return true;
}

export function isFullSizeEqual(a: TransformFullSize, b: TransformFullSize): boolean {
  if (a.width !== b.width) return false;
  if (a.height !== b.height) return false;
  if (a.relativeWidth !== b.relativeWidth) return false;
  if (a.relativeHeight !== b.relativeHeight) return false;
  if (a.padWidth !== b.padWidth) return false;
  if (a.padHeight !== b.padHeight) return false;
  if (a.maxWidth !== b.maxWidth) return false;
  if (a.maxHeight !== b.maxHeight) return false;
  if (a.maxAspect !== b.maxAspect) return false;

  return true;
}

export function isShapeStyleEqual(a: RenderShapeStyle, b: RenderShapeStyle): boolean {
  if (isColorEqual(a.fillColor, b.fillColor)) return false;
  if (a.strokeWeight !== b.strokeWeight) return false;
  if (isColorEqual(a.strokeColor, b.strokeColor)) return false;
  if (islineDashEqual(a.lineDash, b.lineDash)) return false;
  if (a.lineDashOffset !== b.lineDashOffset) return false;
  if (a.gradient !== b.gradient) return false;
  if (a.gradientFade !== b.gradientFade) return false;

  return true;
}

export function isColorEqual(a: Color, b: Color): boolean {
  if (a[0] !== b[0]) return false;
  if (a[1] !== b[1]) return false;
  if (a[2] !== b[2]) return false;
  if (a.length === 4 && b.length === 4) {
    if (a[3] !== b[3]) return false;
  } else if (a.length !== b.length) {
    return false;
  }

  return true;
}

export function islineDashEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;

  for (let i = a.length - 1; i >= 0; i--) {
    if (a[i] !== b[i]) return false;
  }

  return true;
}
